import type { Doc, Id } from "@repo/convex/dataModel";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  useClarificationAssignmentHistory,
  useClarificationRequest,
  useClarificationRequestMessages,
  useClarificationTriageStatus,
  useConsultations,
  usePostClarificationMessage,
  useUpdateClarification,
} from "@/features/clarification/hooks";
import {
  getCurrentUserClarificationDisplay,
  resolveClarificationAuthorDisplay,
} from "@/features/clarification/utils/author-display";
import type { MessageRichTextSubmitPayload } from "@/features/rich-text";
import { useCurrentUser, useOrgUserMap } from "@/hooks";
import {
  type AssignmentEvent,
  type ClarificationChatHeaderRenderContext,
  ClarificationChatView,
} from "./clarification-chat";
import type { ConsultationDisplayStatus, ConsultationView } from "./consultation-types";
import { InviteViaTeamsButton } from "./invite-via-teams-button";

type MessageSource = "app" | "teams" | "claudia";
type ClarificationStatus = "open" | "closed";

type LocalMessage = {
  id: string;
  content: string;
  author: string;
  authorInitials: string;
  timestamp: Date;
  source: MessageSource;
};

type SelectedClarification = {
  id: string;
  title: string;
  status: ClarificationStatus;
  author: string;
  authorInitials: string;
  createdAt: Date;
  highlightedText?: string;
};

export type ClarificationThreadHeaderRenderContext = ClarificationChatHeaderRenderContext & {
  clarification: Doc<"clarificationRequests">;
  /** Invite-via-Teams affordance for custom headers to place (LAT-626). */
  inviteButton: ReactNode;
};

export type ClarificationThreadProps = {
  projectId: Id<"projects">;
  clarificationRequestId: Id<"clarificationRequests">;
  onBack: () => void;
  surface: "full_screen" | "right_panel";
  renderHeader?: (ctx: ClarificationThreadHeaderRenderContext) => ReactNode;
  loadingLabel?: string;
  notFoundLabel?: string;
};

export function ClarificationThread({
  projectId,
  clarificationRequestId,
  onBack,
  surface,
  renderHeader,
  loadingLabel = "Loading thread...",
  notFoundLabel = "Clarification not found",
}: ClarificationThreadProps) {
  const clarification = useClarificationRequest(clarificationRequestId);
  const messagesData = useClarificationRequestMessages(clarificationRequestId);
  const assignmentHistoryData = useClarificationAssignmentHistory(clarificationRequestId);
  const triageStatus = useClarificationTriageStatus(clarificationRequestId);
  const consultations = useConsultations(clarificationRequestId);
  const userMap = useOrgUserMap();
  const currentUser = useCurrentUser();
  const postClarificationMessage = usePostClarificationMessage();
  const updateClarification = useUpdateClarification();
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isAssigningOwner, setIsAssigningOwner] = useState(false);

  const currentUserDisplay = useMemo(
    () => getCurrentUserClarificationDisplay(currentUser),
    [currentUser]
  );

  const selectedClarification = useMemo<SelectedClarification | null>(() => {
    if (!clarification) return null;
    const author = resolveClarificationAuthorDisplay({
      createdBy: clarification.createdBy,
      currentUser,
      userMap,
      source: "app",
    });

    return {
      id: clarification._id,
      title: clarification.title,
      status: clarification.status as ClarificationStatus,
      author: author.label,
      authorInitials: author.initials,
      createdAt: new Date(clarification._creationTime),
      highlightedText: clarification.blockReference?.highlightedText,
    };
  }, [clarification, currentUser, userMap]);

  const messages = useMemo<LocalMessage[]>(() => {
    if (!messagesData) return [];

    return messagesData.map((message) => {
      const source = message.source as MessageSource;
      const author = resolveClarificationAuthorDisplay({
        createdBy: message.createdBy,
        currentUser,
        userMap,
        source,
      });

      return {
        id: message._id,
        content: message.content,
        author: author.label,
        authorInitials: author.initials,
        timestamp: new Date(message._creationTime),
        source,
      };
    });
  }, [messagesData, currentUser, userMap]);

  const assignmentEvents = useMemo<AssignmentEvent[]>(() => {
    if (!assignmentHistoryData) return [];
    return assignmentHistoryData.map((row) => ({
      id: row._id,
      timestamp: new Date(row._creationTime),
      assignedByName: row.assignedByName,
      assignedToName: row.assignedToName,
    }));
  }, [assignmentHistoryData]);

  const consultationViews = useMemo<ConsultationView[]>(() => {
    if (!consultations) return [];
    return consultations.map((consultation) => ({
      id: consultation._id,
      startedAt: new Date(consultation._creationTime),
      displayStatus: consultation.displayStatus as ConsultationDisplayStatus,
      topic: consultation.topic,
      requesterName: consultation.requesterName,
      inviteeNames: consultation.inviteeNames,
      inboundReplyCount: consultation.inboundReplyCount,
      transcriptCount: consultation.transcriptCount,
      webUrl: consultation.webUrl,
      teamsChatId: consultation.teamsChatId,
      summaryMessageId: consultation.summaryMessageId,
      failureMessage: consultation.lastFailure?.message ?? null,
      failureRetryable: consultation.lastFailure?.retryable ?? false,
      cancelledAt: consultation.cancelledAt ? new Date(consultation.cancelledAt) : null,
    }));
  }, [consultations]);

  if (clarification === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-xs text-muted-foreground">
        {notFoundLabel}
      </div>
    );
  }

  if (!clarification || !selectedClarification) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-xs text-muted-foreground">
        {loadingLabel}
      </div>
    );
  }

  const handleSendMessage = async ({ markdown }: MessageRichTextSubmitPayload) => {
    const content = markdown.trim();
    if (!content) return false;

    setIsSendingMessage(true);
    try {
      await postClarificationMessage({
        clarificationRequestId,
        content,
        createdBy: currentUser?._id,
        source: "app",
      });
      return true;
    } catch {
      toast.error("Failed to post reply. Please try again.");
      return false;
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleStatusChange = async (status: ClarificationStatus) => {
    if (selectedClarification.status === status) return;

    setIsUpdatingStatus(true);
    try {
      await updateClarification({ clarificationRequestId, status });
    } catch {
      toast.error("Failed to update clarification status. Please try again.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAssignOwner = async (userId: Id<"users">) => {
    if (clarification.ownerId === userId) return;

    setIsAssigningOwner(true);
    try {
      await updateClarification({ clarificationRequestId, ownerId: userId });
    } catch {
      toast.error("Failed to assign owner. Please try again.");
    } finally {
      setIsAssigningOwner(false);
    }
  };

  const inviteButton =
    selectedClarification.status === "open" ? (
      <InviteViaTeamsButton clarificationRequestId={clarificationRequestId} />
    ) : null;

  return (
    <ClarificationChatView
      mentionProjectId={projectId}
      selectedClarification={selectedClarification}
      messages={messages}
      assignmentEvents={assignmentEvents}
      consultations={consultationViews}
      isTriageActive={triageStatus?.isActive ?? false}
      headerInviteSlot={inviteButton}
      ownerId={clarification.ownerId ?? null}
      onAssignOwner={handleAssignOwner}
      isAssigningOwner={isAssigningOwner}
      onSendMessage={handleSendMessage}
      isSendingMessage={isSendingMessage}
      composerAuthor={currentUserDisplay.label}
      composerInitials={currentUserDisplay.initials}
      onStatusChange={handleStatusChange}
      isUpdatingStatus={isUpdatingStatus}
      onBack={onBack}
      surface={surface}
      renderHeader={
        renderHeader
          ? (ctx) =>
              renderHeader({
                ...ctx,
                clarification,
                inviteButton,
              })
          : undefined
      }
    />
  );
}
